import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  CalendarToday as CalendarIcon,
  Description as DocumentIcon,
  Home as HomeIcon,
  Autorenew as RenewIcon,
  Edit as EditIcon,
  WarningAmber as WarningIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { UTILITY_COLORS, getCategoryColor } from '../theme';
import ManualCertificateEntryDialog from './ManualCertificateEntryDialog';
import EnhancedCertificateCard from './EnhancedCertificateCard';
import FlatLimitDialog, { FlatLimitInfo, FlatUnit } from './FlatLimitDialog';

class FlatCancelled extends Error {}

interface ExtractedCertificate {
  cert_data: {
    certificate_type: string;
    certificate_type_category?: string;
    property_address: string;
    issue_date?: string;
    expiry_date?: string;
    [key: string]: any;
  };
  file_info: any;
  index: number;
  filename: string;
}

interface FailedFile {
  filename: string;
  error: string;
  manual_entry_available?: boolean;
  ai_explanation?: string;
  file_info?: {
    filename: string;
    original_filename: string;
    file_path: string;
  };
}

interface BulkUploadSummaryDialogProps {
  open: boolean;
  extractedCertificates: ExtractedCertificate[];
  failedFiles: FailedFile[];
  hasExistingCertificates: boolean;
  allCertificates: any[];
  onClose: () => void;
  onComplete: (newlyAddedIds?: string[]) => void;
  token: string;
  matchFoundCount?: number;
  matchFoundRenewals?: any[];
  hasVisitedRenewals?: boolean;
  setHasVisitedRenewals?: (visited: boolean) => void;
  duplicates?: any[];
  semanticDuplicates?: any[];
  olderThanExisting?: any[];
}


const BulkUploadSummaryDialog: React.FC<BulkUploadSummaryDialogProps> = ({
  open,
  extractedCertificates,
  failedFiles,
  hasExistingCertificates,
  allCertificates,
  onClose,
  onComplete,
  token,
  matchFoundCount = 0,
  matchFoundRenewals = [],
  hasVisitedRenewals = false,
  setHasVisitedRenewals,
  duplicates = [],
  semanticDuplicates = [],
  olderThanExisting = [],
}) => {
  const navigate = useNavigate();
  const { palette } = useTheme();
  const [processing, setProcessing] = useState<{ [key: number]: boolean }>({});
  const [completed, setCompleted] = useState<{ [key: number]: boolean }>({});
  const [renewSelections, setRenewSelections] = useState<{ [key: number]: string }>({});
  const [addingAll, setAddingAll] = useState(false);
  const [savingForLater, setSavingForLater] = useState(false);
  const [hasAddedAny, setHasAddedAny] = useState(false);
  const [newlyAddedCertIds, setNewlyAddedCertIds] = useState<string[]>([]);
  const [manualEntryDialogOpen, setManualEntryDialogOpen] = useState(false);
  const [selectedManualEntry, setSelectedManualEntry] = useState<FailedFile | null>(null);
  const [semanticProcessing, setSemanticProcessing] = useState<{ [key: number]: boolean }>({});
  const [semanticCompleted, setSemanticCompleted] = useState<{ [key: number]: boolean }>({});
  const [staleProcessing, setStaleProcessing] = useState<{ [key: number]: boolean }>({});
  const [staleCompleted, setStaleCompleted] = useState<{ [key: number]: boolean }>({});

  // MUI Dialog never unmounts — reset all transient state each time the dialog opens
  // so stale completed/processing state from a previous upload session doesn't leak in.
  useEffect(() => {
    if (open) {
      setProcessing({});
      setCompleted({});
      setRenewSelections({});
      setAddingAll(false);
      setSavingForLater(false);
      setHasAddedAny(false);
      setNewlyAddedCertIds([]);
      setSemanticProcessing({});
      setSemanticCompleted({});
      setStaleProcessing({});
      setStaleCompleted({});
    }
  }, [open, extractedCertificates]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getFlatNumbers = (cert: ExtractedCertificate) => {
    const flat = cert.cert_data.flat_number;
    if (!flat || !Array.isArray(flat) || flat.length === 0) return null;
    return flat.join(', ');
  };

  // Saving can be refused because it would add a flat beyond the bought allowance; the user then
  // buys a flat or re-points the certificate to an existing one, and the save is retried.
  const [flatBlock, setFlatBlock] = useState<FlatLimitInfo | null>(null);
  const flatResolver = useRef<((r: { unit?: FlatUnit } | null) => void) | null>(null);
  const askFlatResolution = (info: FlatLimitInfo) =>
    new Promise<{ unit?: FlatUnit } | null>(resolve => { flatResolver.current = resolve; setFlatBlock(info); });
  const resolveFlatBlock = (result: { unit?: FlatUnit } | null) => {
    setFlatBlock(null);
    flatResolver.current?.(result);
    flatResolver.current = null;
  };

  const addAsNew = async (certData: any, fileInfo: any) => {
    let data = certData;
    for (;;) {
      try {
        return await axios.post(
          `${config.apiUrl}/api/pending-renewals/add-as-new`,
          { cert_data: data, file_info: fileInfo },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err: any) {
        if (err.response?.status === 403 && err.response.data?.code === 'FLAT_LIMIT_REACHED') {
          const resolution = await askFlatResolution(err.response.data);
          if (!resolution) throw new FlatCancelled();
          if (resolution.unit) {
            data = {
              ...data,
              normalized_address: resolution.unit.normalized_address,
              flat_number: resolution.unit.flat === 'Whole Property' ? [] : [resolution.unit.flat],
            };
          }
          continue;
        }
        throw err;
      }
    }
  };

  const handleAddSingle = async (index: number, cert: ExtractedCertificate) => {
    setProcessing(prev => ({ ...prev, [index]: true }));
    try {
      const response = await addAsNew(cert.cert_data, cert.file_info);
      if (response.data.certificate?.id) {
        setNewlyAddedCertIds(prev => [...prev, response.data.certificate.id]);
      }
      setCompleted(prev => ({ ...prev, [index]: true }));
      setHasAddedAny(true);
    } catch (error) {
      if (!(error instanceof FlatCancelled)) {
        console.error('Failed to add certificate:', error);
        alert((error as any).response?.data?.error || 'Failed to add certificate');
      }
    } finally {
      setProcessing(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleRenewSingle = async (index: number, cert: ExtractedCertificate) => {
    const selectedCertId = renewSelections[index];
    if (!selectedCertId) { alert('Please select a certificate to renew'); return; }
    setProcessing(prev => ({ ...prev, [index]: true }));
    try {
      await axios.post(
        `${config.apiUrl}/api/certificates/${selectedCertId}/renew-direct`,
        { cert_data: cert.cert_data, file_info: cert.file_info },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompleted(prev => ({ ...prev, [index]: true }));
      setHasAddedAny(true);
    } catch (error) {
      console.error('Failed to renew certificate:', error);
      alert('Failed to renew certificate');
    } finally {
      setProcessing(prev => ({ ...prev, [index]: false }));
    }
  };

  // One at a time: each add can change which flats exist, which the flat cap check depends on.
  const handleAddAll = async () => {
    setAddingAll(true);
    const addedIds: string[] = [];
    try {
      for (let i = 0; i < extractedCertificates.length; i++) {
        if (completed[i]) continue;
        const cert = extractedCertificates[i];
        setProcessing(prev => ({ ...prev, [i]: true }));
        try {
          const response = await addAsNew(cert.cert_data, cert.file_info);
          if (response.data.certificate?.id) addedIds.push(response.data.certificate.id);
          setCompleted(prev => ({ ...prev, [i]: true }));
        } catch (error) {
          if (error instanceof FlatCancelled) break;
          console.error(`Failed to add ${cert.filename}:`, error);
          const message = (error as any).response?.data?.error;
          if (message) { alert(message); break; }
        } finally {
          setProcessing(prev => ({ ...prev, [i]: false }));
        }
      }
      setNewlyAddedCertIds(prev => [...prev, ...addedIds]);
      if (addedIds.length) setHasAddedAny(true);
      setTimeout(() => onComplete([...newlyAddedCertIds, ...addedIds]), 500);
    } finally {
      setAddingAll(false);
    }
  };

  const handleAddSemantic = async (index: number, semDup: any) => {
    setSemanticProcessing(prev => ({ ...prev, [index]: true }));
    try {
      const response = await addAsNew(semDup.cert_data, semDup.file_info);
      if (response.data.certificate?.id) {
        setNewlyAddedCertIds(prev => [...prev, response.data.certificate.id]);
      }
      setSemanticCompleted(prev => ({ ...prev, [index]: true }));
      setHasAddedAny(true);
    } catch (error) {
      if (!(error instanceof FlatCancelled)) {
        console.error('Failed to add certificate:', error);
        alert((error as any).response?.data?.error || 'Failed to add certificate');
      }
    } finally {
      setSemanticProcessing(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleAddStale = async (index: number, stale: any) => {
    setStaleProcessing(prev => ({ ...prev, [index]: true }));
    try {
      const response = await addAsNew(stale.cert_data, stale.file_info);
      if (response.data.certificate?.id) {
        setNewlyAddedCertIds(prev => [...prev, response.data.certificate.id]);
      }
      setStaleCompleted(prev => ({ ...prev, [index]: true }));
      setHasAddedAny(true);
    } catch (error) {
      if (!(error instanceof FlatCancelled)) {
        console.error('Failed to add certificate:', error);
        alert((error as any).response?.data?.error || 'Failed to add certificate');
      }
    } finally {
      setStaleProcessing(prev => ({ ...prev, [index]: false }));
    }
  };

  const allCompleted = extractedCertificates.every((_, index) => completed[index]);
  const hasIncompleteItems = !allCompleted && extractedCertificates.length > 0;
  const incompleteCount = extractedCertificates.filter((_, i) => !completed[i]).length;

  // Every close path (X, backdrop, footer button) parks whatever wasn't added so it can be resumed later.
  const handleClose = async (): Promise<boolean> => {
    if (addingAll || savingForLater) return false;
    if (hasIncompleteItems) {
      setSavingForLater(true);
      try {
        await axios.post(
          `${config.apiUrl}/api/pending-certificates`,
          {
            items: extractedCertificates
              .filter((_, i) => !completed[i])
              .map(c => ({ cert_data: c.cert_data, file_info: c.file_info })),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to save certificates for later:', error);
        alert('Could not save the remaining certificates for later. They have not been closed — please try again.');
        setSavingForLater(false);
        return false;
      }
      setSavingForLater(false);
    }
    if (hasAddedAny) onComplete(newlyAddedCertIds);
    else onClose();
    return true;
  };

  const handleMoreOptions = async () => {
    if (await handleClose()) {
      setHasVisitedRenewals?.(true);
      navigate('/approvals?tab=manual');
    }
  };

  return (
    <>
    <FlatLimitDialog info={flatBlock} token={token} onResolve={resolveFlatBlock} />
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            {hasExistingCertificates ? 'Review Certificates' : 'Extraction Complete'}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={addingAll || savingForLater}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Stats row */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          {extractedCertificates.length > 0 && (
            <Chip icon={<CheckIcon />} label={`${extractedCertificates.length} extracted`} color="success" size="small" variant="outlined" />
          )}
          {duplicates.length > 0 && (
            <Chip icon={<DocumentIcon />} label={`${duplicates.length} duplicate${duplicates.length > 1 ? 's' : ''}`} size="small" variant="outlined" />
          )}
          {semanticDuplicates.length > 0 && (
            <Chip icon={<WarningIcon />} label={`${semanticDuplicates.length} possible duplicate${semanticDuplicates.length > 1 ? 's' : ''}`} color="warning" size="small" variant="outlined" />
          )}
          {olderThanExisting.length > 0 && (
            <Chip icon={<ErrorIcon />} label={`${olderThanExisting.length} older than existing`} color="error" size="small" variant="outlined" />
          )}
          {failedFiles.length > 0 && (
            <Chip icon={<ErrorIcon />} label={`${failedFiles.length} failed`} color="error" size="small" variant="outlined" />
          )}
          {matchFoundCount > 0 && (
            <Chip icon={<RenewIcon />} label={`${matchFoundCount} renewal${matchFoundCount > 1 ? 's' : ''} detected`} color="warning" size="small" variant="outlined" />
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Renewal comparison cards */}
        {matchFoundCount > 0 && matchFoundRenewals.length > 0 && !hasVisitedRenewals && matchFoundRenewals.map((renewal, index) => {
          const category = renewal.certificate_type_category || 'Other';
          const categoryColor = getCategoryColor(category);

          const uploadedAddress = renewal.normalized_address || '';
          const existingAddress = renewal.existing_normalized_address || '';

          const uploadedFlats: string[] = Array.isArray(renewal.flat_number) ? renewal.flat_number : [];
          const existingFlats: string[] = Array.isArray(renewal.existing_flat_number) ? renewal.existing_flat_number : [];
          const validUploadedFlats = uploadedFlats.filter((f: string) => f && f.toLowerCase() !== 'n/a' && f.toLowerCase() !== 'whole property');
          const validExistingFlats = existingFlats.filter((f: string) => f && f.toLowerCase() !== 'n/a' && f.toLowerCase() !== 'whole property');
          const showFlatRow = validUploadedFlats.length > 0 || validExistingFlats.length > 0;

          const existingIssue  = renewal.existing_issue_date  || '';
          const existingExpiry = renewal.existing_expiry_date || '';
          const newIssue       = renewal.issue_date           || '';
          const newExpiry      = renewal.expiry_date          || '';

          const cellBase   = { py: 1.25, borderBottom: '1px solid rgba(125,211,252,0.35)' };
          const labelSx    = { ...cellBase, fontSize: '0.85rem', fontWeight: 600, color: palette.tokens.muted };
          const addrSx     = { ...cellBase, fontSize: '0.95rem', fontWeight: 400, color: UTILITY_COLORS.comparisonOld };
          const oldDateSx  = { ...cellBase, fontSize: '0.95rem', fontWeight: 400, color: palette.tokens.mutedSoft };
          const mutedSx    = { ...cellBase, fontSize: '0.95rem', fontWeight: 400, color: palette.tokens.mutedSoft };
          const newDateSx  = { ...cellBase, fontSize: '0.95rem', fontWeight: 700, color: UTILITY_COLORS.comparisonNewStrong };
          const fileSx     = { ...cellBase, fontSize: '0.78rem', color: palette.tokens.mutedSoft, fontWeight: 400 };

          return (
            <Paper key={index} elevation={0} sx={{ p: 2.5, mb: 1.5, bgcolor: '#f0f9ff', border: '1.5px solid #7dd3fc', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Chip
                  label={category}
                  size="small"
                  sx={{ bgcolor: categoryColor, color: palette.tokens.white, fontWeight: 600, fontSize: '0.75rem', height: 24, px: 0.5 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => { setHasVisitedRenewals?.(true); navigate('/approvals?tab=manual'); }}
                  sx={{ borderColor: '#0ea5e9', color: '#0369a1', fontWeight: 600, '&:hover': { borderColor: '#0284c7', bgcolor: '#e0f2fe' } }}
                >
                  Review renewal →
                </Button>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '82px 1fr 1fr', columnGap: 2.5 }}>
                {/* Column headers */}
                <Box />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: palette.tokens.mutedSoft, textTransform: 'uppercase', letterSpacing: 0.8, pb: 1, borderBottom: '2px solid #7dd3fc' }}>
                  Existing
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: UTILITY_COLORS.comparisonNewAccent, textTransform: 'uppercase', letterSpacing: 0.8, pb: 1, borderBottom: '2px solid #7dd3fc' }}>
                  Uploaded (new)
                </Typography>

                {/* Address — ✓ only when non-empty */}
                <Typography sx={labelSx}>Address</Typography>
                <Typography sx={addrSx}>{existingAddress ? `${existingAddress} ✓` : '—'}</Typography>
                <Typography sx={addrSx}>{uploadedAddress ? `${uploadedAddress} ✓` : '—'}</Typography>

                {/* Flat */}
                {showFlatRow && <>
                  <Typography sx={labelSx}>Flat</Typography>
                  <Typography sx={addrSx}>{validExistingFlats.join(', ') || '—'}</Typography>
                  <Typography sx={addrSx}>{validUploadedFlats.join(', ') || '—'}</Typography>
                </>}

                {/* Issue date — strikethrough only when old date exists; ✓ only when new date exists */}
                <Typography sx={labelSx}>Issued</Typography>
                <Typography sx={existingIssue ? oldDateSx : mutedSx}>{existingIssue || '—'}</Typography>
                <Typography sx={newIssue ? newDateSx : mutedSx}>{newIssue ? `${newIssue} ✓` : '—'}</Typography>

                {/* Expiry date */}
                <Typography sx={labelSx}>Expires</Typography>
                <Typography sx={existingExpiry ? oldDateSx : mutedSx}>{existingExpiry || '—'}</Typography>
                <Typography sx={newExpiry ? newDateSx : mutedSx}>{newExpiry ? `${newExpiry} ✓` : '—'}</Typography>

                {/* Files */}
                <Typography sx={fileSx}>File</Typography>
                <Typography sx={fileSx} noWrap>{renewal.existing_original_filename || '—'}</Typography>
                <Typography sx={fileSx} noWrap>{renewal.original_filename || renewal.filename || '—'}</Typography>
              </Box>
            </Paper>
          );
        })}

        {/* Fallback: renewal detected but no detail data */}
        {matchFoundCount > 0 && matchFoundRenewals.length === 0 && !hasVisitedRenewals && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => { setHasVisitedRenewals?.(true); navigate('/approvals?tab=manual'); }} sx={{ textDecoration: 'underline' }}>
                Review
              </Button>
            }
          >
            <strong>{matchFoundCount} certificate{matchFoundCount > 1 ? 's' : ''}</strong> matched existing records — review them as renewals.
          </Alert>
        )}

        {hasExistingCertificates && extractedCertificates.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Add as new or mark as a renewal of an existing certificate.
          </Alert>
        )}

        {hasIncompleteItems && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Anything you don't add now is saved under Approvals → Manual uploads, so you can finish later.
          </Typography>
        )}

        {/* Certificate cards */}
        {extractedCertificates.map((cert, index) => {
          const category = cert.cert_data.certificate_type_category || 'Other';
          const categoryColor = getCategoryColor(category);
          const flatNumbers = getFlatNumbers(cert);
          const address = cert.cert_data.normalized_address || cert.cert_data.property_address;

          return (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 2,
                mb: 1.5,
                border: '1.5px solid',
                borderColor: completed[index] ? 'success.main' : palette.tokens.hairline,
                borderRadius: 2,
                opacity: completed[index] ? 0.6 : 1,
                transition: 'border-color 0.2s, opacity 0.2s',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Category chip */}
                  <Chip
                    label={category}
                    size="small"
                    sx={{ mb: 1, bgcolor: categoryColor, color: palette.tokens.white, fontWeight: 600, fontSize: '0.68rem', height: 20 }}
                  />

                  {/* Certificate title */}
                  <Typography fontWeight={700} sx={{ fontSize: '0.92rem', mb: 0.75, textTransform: 'capitalize', lineHeight: 1.3 }}>
                    {cert.cert_data.certificate_type || cert.filename}
                  </Typography>

                  {/* Address + flat */}
                  {address && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
                      <HomeIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {address}{flatNumbers ? ` · ${flatNumbers}` : ''}
                      </Typography>
                    </Box>
                  )}

                  {/* Dates */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <CalendarIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(cert.cert_data.issue_date)} → {formatDate(cert.cert_data.expiry_date)}
                    </Typography>
                  </Box>

                  {/* Filename (secondary) */}
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: 'block' }}>
                    {cert.filename}
                  </Typography>

                  {/* Renewal selector */}
                  {hasExistingCertificates && !completed[index] && (
                    <Box sx={{ mt: 1.5 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Renew existing (optional)</InputLabel>
                        <Select
                          value={renewSelections[index] || ''}
                          onChange={(e) => setRenewSelections(prev => ({ ...prev, [index]: e.target.value }))}
                          label="Renew existing (optional)"
                          disabled={processing[index]}
                        >
                          <MenuItem value=""><em>Add as new</em></MenuItem>
                          {allCertificates.map((existing) => (
                            <MenuItem key={existing.id} value={existing.id}>
                              <Box>
                                <Typography variant="body2" noWrap>{existing.certificate_type}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>{existing.property_address}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>

                {/* Action */}
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {!completed[index] && (
                    <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={handleMoreOptions} disabled={processing[index] || addingAll || savingForLater}>
                      More options
                    </Button>
                  )}
                  {!completed[index] ? (
                    renewSelections[index] ? (
                      <Button variant="contained" size="small" startIcon={<RenewIcon />} onClick={() => handleRenewSingle(index, cert)} disabled={processing[index]}>
                        Renew
                      </Button>
                    ) : (
                      <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleAddSingle(index, cert)} disabled={processing[index]}>
                        Add
                      </Button>
                    )
                  ) : (
                    <Chip icon={<CheckIcon />} label="Added" color="success" size="small" />
                  )}
                </Box>
              </Box>
            </Paper>
          );
        })}

        {/* Duplicates */}
        {duplicates.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              You have already added this certificate, so it was not added again to avoid duplication. The certificate you just uploaded is an exact match of the one below.
            </Alert>
            {duplicates.map((dup, index) => {
              // Prefer the full record from the portfolio so the card matches the dashboard
              const existing = allCertificates.find((c: any) => c.id === dup.certificate_id) || {
                id: dup.certificate_id,
                certificate_type: dup.certificate_type,
                property_address: dup.property_address,
                issue_date: dup.issue_date,
                expiry_date: dup.expiry_date,
                original_filename: dup.original_filename,
              };
              const viewInDashboard = () => {
                onClose();
                navigate(`/dashboard?cert=${dup.certificate_id}`);
              };
              return (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <EnhancedCertificateCard certificate={existing} showSelection={false} hideActions onView={viewInDashboard} />
                  </Box>
                  <Tooltip title="Show in your certificates">
                    <IconButton onClick={viewInDashboard} size="small" sx={{ mt: 1 }}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Older than existing — the upload is outdated relative to what's in the portfolio */}
        {olderThanExisting.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Older than existing ({olderThanExisting.length})
            </Typography>
            <Alert severity="error" sx={{ mt: 1, mb: 1.5 }}>
              You already have a newer version of this certificate in your portfolio. This upload appears to be an older document — adding it may introduce outdated information. You can still add it if you need to keep the historical record.
            </Alert>
            {olderThanExisting.map((stale: any, index: number) => {
              const category = stale.certificate_type_category || 'Other';
              const categoryColor = getCategoryColor(category);
              return (
                <Paper key={index} elevation={0} sx={{ p: 2, mb: 1, bgcolor: '#fff1f2', border: `1.5px solid ${palette.tokens.error}`, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Chip
                        label={category}
                        size="small"
                        sx={{ mb: 0.75, bgcolor: categoryColor, color: palette.tokens.white, fontWeight: 600, fontSize: '0.68rem', height: 20 }}
                      />
                      <Typography fontWeight={700} sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                        {stale.certificate_type || stale.filename}
                      </Typography>
                      {stale.property_address && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.3 }}>
                          <HomeIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary" noWrap>{stale.property_address}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                          Upload: {formatDate(stale.cert_data?.expiry_date)}
                          {' · '}
                          <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>
                            Existing: {formatDate(stale.expiry_date)}
                          </Box>
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.disabled">
                        Existing file: {stale.original_filename}
                      </Typography>
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                      {staleCompleted[index] ? (
                        <Chip icon={<CheckIcon />} label="Added" color="success" size="small" />
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleAddStale(index, stale)}
                          disabled={staleProcessing[index]}
                          sx={{ borderColor: palette.tokens.error, color: '#991b1b', '&:hover': { borderColor: palette.tokens.error, bgcolor: '#fff1f2' } }}
                        >
                          Add anyway
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Semantic duplicates — soft warning with field-by-field comparison */}
        {semanticDuplicates.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Possible duplicates ({semanticDuplicates.length})
            </Typography>
            {semanticDuplicates.map((semDup: any, index: number) => {
              const category = semDup.certificate_type_category || 'Other';
              const categoryColor = getCategoryColor(category);

              const uploadedAddress  = semDup.cert_data?.normalized_address || '—';
              const existingAddress  = semDup.normalized_address || '—';
              const uploadedFlat     = Array.isArray(semDup.cert_data?.flat_number) && semDup.cert_data.flat_number.length
                ? semDup.cert_data.flat_number.join(', ') : null;
              const existingFlat     = Array.isArray(semDup.flat_number) && semDup.flat_number.length
                ? semDup.flat_number.join(', ') : null;
              const uploadedIssue    = formatDate(semDup.cert_data?.issue_date);
              const existingIssue    = formatDate(semDup.issue_date);
              const uploadedExpiry   = formatDate(semDup.cert_data?.expiry_date);
              const existingExpiry   = formatDate(semDup.expiry_date);
              const uploadedFile     = semDup.file_info?.original_filename || semDup.filename;

              const cellBase = { py: 1.25, borderBottom: '1px solid rgba(251,191,36,0.3)' };
              const labelSx  = { ...cellBase, fontSize: '0.85rem', fontWeight: 600, color: palette.tokens.muted };
              const uploadSx = { ...cellBase, fontSize: '0.95rem', fontWeight: 400, color: UTILITY_COLORS.comparisonOld };
              const matchSx  = { ...cellBase, fontSize: '0.95rem', fontWeight: 700, color: UTILITY_COLORS.comparisonNewStrong };
              const fileSx   = { ...cellBase, fontSize: '0.78rem', color: palette.tokens.mutedSoft, fontWeight: 400 };

              return (
                <Paper key={index} elevation={0} sx={{ p: 2.5, mb: 1.5, bgcolor: '#fffbeb', border: '1.5px solid #fbbf24', borderRadius: 2 }}>
                  {/* Header: chip + action */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={category}
                      size="small"
                      sx={{ bgcolor: categoryColor, color: palette.tokens.white, fontWeight: 600, fontSize: '0.75rem', height: 24, px: 0.5 }}
                    />
                    {semanticCompleted[index] ? (
                      <Chip icon={<CheckIcon />} label="Added" color="success" size="small" />
                    ) : (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleAddSemantic(index, semDup)}
                        disabled={semanticProcessing[index]}
                        sx={{ borderColor: '#f59e0b', color: '#92400e', fontWeight: 600, '&:hover': { borderColor: '#d97706', bgcolor: '#fef3c7' } }}
                      >
                        Add anyway
                      </Button>
                    )}
                  </Box>

                  {/* Comparison grid */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '82px 1fr 1fr', columnGap: 2.5 }}>
                    {/* Column headers */}
                    <Box />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: palette.tokens.mutedSoft, textTransform: 'uppercase', letterSpacing: 0.8, pb: 1, borderBottom: '2px solid #fbbf24' }}>
                      Uploaded
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: UTILITY_COLORS.comparisonNewAccent, textTransform: 'uppercase', letterSpacing: 0.8, pb: 1, borderBottom: '2px solid #fbbf24' }}>
                      In portfolio
                    </Typography>

                    {/* Address */}
                    <Typography sx={labelSx}>Address</Typography>
                    <Typography sx={uploadSx}>{uploadedAddress}</Typography>
                    <Typography sx={matchSx}>{existingAddress} ✓</Typography>

                    {/* Flat (only when present) */}
                    {(uploadedFlat || existingFlat) && <>
                      <Typography sx={labelSx}>Flat</Typography>
                      <Typography sx={uploadSx}>{uploadedFlat || '—'}</Typography>
                      <Typography sx={matchSx}>{existingFlat || '—'} ✓</Typography>
                    </>}

                    {/* Issue date */}
                    <Typography sx={labelSx}>Issued</Typography>
                    <Typography sx={uploadSx}>{uploadedIssue}</Typography>
                    <Typography sx={matchSx}>{existingIssue} ✓</Typography>

                    {/* Expiry date */}
                    <Typography sx={labelSx}>Expires</Typography>
                    <Typography sx={uploadSx}>{uploadedExpiry}</Typography>
                    <Typography sx={matchSx}>{existingExpiry} ✓</Typography>

                    {/* Files */}
                    <Typography sx={fileSx}>File</Typography>
                    <Typography sx={fileSx} noWrap>{uploadedFile}</Typography>
                    <Typography sx={fileSx} noWrap>{semDup.original_filename}</Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Failed files */}
        {failedFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {failedFiles.map((file, index) => (
              <Paper key={index} elevation={0} sx={{ p: 2, mb: 1, bgcolor: '#fff8f1', border: '1px solid #ffb74d', borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>{file.filename}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">{file.error}</Typography>
                {file.ai_explanation && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                    {file.ai_explanation}
                  </Typography>
                )}
                {file.manual_entry_available && file.file_info && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => { setSelectedManualEntry(file); setManualEntryDialogOpen(true); }}
                      sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
                    >
                      Add manually
                    </Button>
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button onClick={handleClose} disabled={addingAll || savingForLater} variant="outlined">
          {savingForLater
            ? 'Saving…'
            : hasIncompleteItems
              ? 'Save ' + incompleteCount + ' for later'
              : hasAddedAny ? 'Done' : 'Close'}
        </Button>
        {hasIncompleteItems && (
          <Button variant="contained" color="primary" startIcon={addingAll ? null : <AddIcon />} onClick={handleAddAll} disabled={addingAll || savingForLater}>
            {addingAll ? 'Adding…' : 'Add All (' + incompleteCount + ')'}
          </Button>
        )}
      </DialogActions>

      {selectedManualEntry?.file_info && (
        <ManualCertificateEntryDialog
          open={manualEntryDialogOpen}
          onClose={() => { setManualEntryDialogOpen(false); setSelectedManualEntry(null); }}
          onSuccess={(certId) => {
            setHasAddedAny(true);
            if (certId) setNewlyAddedCertIds(prev => [...prev, certId]);
          }}
          aiExplanation={selectedManualEntry.ai_explanation || 'AI could not extract certificate data'}
          fileInfo={selectedManualEntry.file_info}
        />
      )}
    </Dialog>
    </>
  );
};

export default BulkUploadSummaryDialog;
